import { getState } from '../store';
import { dispatchAuthFailure, isPermanentAuthFailure } from '../utils/auth-failure';
import { API_BASE } from '../utils/api-base';

// Bug 3 (v0.79.35) — API_BASE is empty in both prod and dev. Prod /api/*
// resolves against the parent host (autokvest.ru) where Safari uses h3
// and bypasses the wedge; dev relies on Vite's /api/* proxy. Kept as a
// named import so a future fix can re-introduce a hostname-aware base
// without touching every fetch site. See api-base.ts.
const BASE = API_BASE;

interface ApiError {
  error: string;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  extraHeaders?: Record<string, string>,
  signal?: AbortSignal,
): Promise<T> {
  const opts: RequestInit = {
    method,
    headers: { 'Content-Type': 'application/json', ...(extraHeaders || {}) },
  };
  if (body) opts.body = JSON.stringify(body);
  if (signal) opts.signal = signal;

  const res = await fetch(`${BASE}${path}`, opts);
  // v0.79.18 — detect permanent auth failure BEFORE throwing. Caller's
  // catch{} continues to work (we still throw); side-effect is the
  // CustomEvent that main.ts handler converts into logout flow.
  if (isPermanentAuthFailure(res)) {
    dispatchAuthFailure(`request-${res.status}-${path}`);
  }
  if (!res.ok) {
    const err: ApiError = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
    throw new Error(err.error);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

/**
 * Returns the player-auth header block for mutating endpoints. Reads
 * `playerToken` from the game store so callers don't need to thread it
 * through every API signature. Empty object if the token isn't set yet
 * (pre-join or legacy session) — backend will return 401 in that case.
 */
function authHeaders(): Record<string, string> {
  const token = getState().playerToken;
  return token ? { 'X-Player-Token': token } : {};
}

// --- Types ---

export interface ResolveCodeResponse {
  type: 'team' | 'spectator' | 'legacy';
  teamName?: string;
  gameTitle: string;
  allowSpectator: boolean;
  // When true, non-captain team members can send chat messages. The join form
  // must require a nickname from every player so chat messages have a sender.
  allowTeamChat?: boolean;
  // True when the resolved code is the team's captain code.
  isCaptainCode?: boolean;
}

export interface JoinGameResponse {
  playerId: string;
  playerToken?: string; // opaque per-player auth token, sent as X-Player-Token
  gameCode: string;
  gameTitle: string;
  organizerPhone: string;
  firstRiddleKey: string;
  salt?: string; // game pepper — needed for per-field bcrypt site code check
  locations: LocationData[];
  gameStarted: boolean;
  isCaptain?: boolean; // server-decided captain flag (true when joined via captainCode)
  allowTeamChat?: boolean;
  canChat?: boolean; // this player is allowed to send chat messages

  fullOffline?: boolean;
  allowManualSkip?: boolean;
  role?: string;
  teamName?: string;
  resumeLocationIndex?: number;
  resumeKey?: string; // key to decrypt riddle at resumeLocationIndex (when > 0)
  lobbyChatMode?: string;
  gameRules?: string | null;
  demoSpeedMultiplier?: number;
  /** Session 53 — plaintext per-location answers for the demo bot.
   *  ONLY present in createDemoSession responses (org-token gated). */
  demoScript?: Array<{
    locationId: string;
    answerType: 'COORDINATES' | 'ADDRESS';
    lat?: number;
    lon?: number;
    city?: string;
    street?: string;
    house?: string;
    siteCode: string;
  }>;
  /** Session 50 post-ship fix: server-stamped timestamp (ms epoch) for when
   *  the team entered the CURRENT location (= resumeLocationIndex). Late
   *  joiners seed `state.locationStartedAt` from this instead of
   *  `Date.now()` so their timer + hint-delay schedule match the rest of
   *  the team. Null when server couldn't anchor (pre-start, pre-migration
   *  legacy row, spectator) — client falls back to `Date.now()`. */
  locationEnteredAt?: number | null;
  /** Session 50 post-ship fix #2 — server's wall clock at response time.
   *  Pairs with `locationEnteredAt` so the PWA can compute a
   *  clock-skew-corrected local anchor:
   *    `state.locationStartedAt = Date.now() - (serverNow - locationEnteredAt)`
   *  Without this, a device clock that's N seconds off the server shows
   *  N seconds wrong elapsed time. */
  serverNow?: number;
}

export interface EncryptedHintData {
  encryptedText: string;
  delaySec: number;
  orderIndex: number;
  type?: string; // "text" | "auto_navigate"
}

export interface LocationData {
  id: string;
  orderIndex: number;
  answerType: 'ADDRESS' | 'COORDINATES';
  geofenceRadiusM: number;
  timeLimitMin: number;
  hintDelayMin: number;
  encryptedRiddleText: string;
  encryptedRiddleHint: string;
  encryptedAnswerData: string;
  encryptedSiteCode: string;
  answerHash: string;
  siteCodeHash: string;
  siteCodeDigitsOnly?: boolean;
  siteCodeRequired?: boolean;
  siteCodeCount?: number;
  minRequiredCodes?: number;
  /** Session 53 — per-code required flags, parallel to the pipe-separated
   *  site-code hashes. Null → legacy row; PWA synthesizes from
   *  `siteCodeRequired` (all-true / all-false). */
  siteCodeRequiredFlags?: boolean[];
  mediaUrl?: string;
  mediaType?: string; // IMAGE, VIDEO, AUDIO
  // Additional media items attached to this location; PWA renders them
  // after the primary. Mirrors backend `LocationRawData.additionalMedia`.
  additionalMedia?: Array<{ url: string; type: string }>;
  encryptedHints?: EncryptedHintData[];
}

/**
 * One team's snapshot for the spectator/organizer progress board. Mirrors
 * backend `TeamProgress` (ru.autoquest.models.TeamProgress). If you rename
 * fields on the backend, update this interface — the types do not cross
 * the JSON boundary automatically.
 */
export interface TeamProgress {
  nickname: string;
  currentLocationIndex: number;
  totalLocations: number;
  correctAnswers: number;
  wrongAnswers: number;
  lastActivityAt: number | null;
  finished: boolean;
}

/**
 * Response of `GET /api/games/{code}/progress` AND payload of the
 * spectator WebSocket `PROGRESS_UPDATE` event (wrapped in
 * `SpectatorEvent.progress`). Mirrors backend `GameProgress`.
 */
export interface GameProgress {
  teams: TeamProgress[];
}

export interface VerifyGeoResponse {
  correct: boolean;
  message: string;
  // Geocoded coordinates of the location target. Server returns them after a
  // successful geo verification so the PWA can build a Yandex Maps URL with
  // precise coordinates instead of relying on the textual address.
  targetLat?: number;
  targetLon?: number;
}

export interface SubmitAnswerResponse {
  correct: boolean;
  message: string;
  nextRiddleKey?: string;
  nextEncryptedRiddle?: string;
  nextEncryptedHint?: string; // legacy single hint
  nextEncryptedHints?: EncryptedHintData[]; // multiple hints
  advancedWithoutAnswer?: boolean;
  penaltyMs?: number;
  /** Server-stamped anchor for the team's current location after this call.
   *  Success: fresh stamp from advanceTeam. Idempotent early-return: observed
   *  current team anchor. The PWA pairs with `serverNow` and runs the value
   *  through `anchorFromServer()` so the timer stays aligned with teammates
   *  regardless of device clock skew. `null` on legacy/offline wire paths. */
  locationEnteredAt?: number | null;
  /** Server wall-clock at response time. Paired with `locationEnteredAt` for
   *  clock-skew correction via `computeLocalAnchor`. Always present post-fix
   *  (backend has `@EncodeDefault(ALWAYS)`); optional in TS for pre-deploy-window
   *  responses that predate the contract. */
  serverNow?: number;
}

/** Session 70 (#13) — one row per (message, emoji). `mine` = viewer
 *  reacted. Comes pre-populated on history fetches; WS payloads carry
 *  `mine=false` and the client patches via actorPlayerId. */
export interface ReactionTallyData {
  emoji: string;
  count: number;
  mine: boolean;
}

export interface ChatMessageData {
  id: string;
  teamName: string;
  senderType: string; // PLAYER, ORGANIZER
  message: string;
  timestamp: number;
  mediaUrl?: string;
  /** Session 70 (#13) — optional because older server builds don't include. */
  reactions?: ReactionTallyData[];
  /** Session 87 — see store.ts ChatMessage notes. Optional for back-compat
   *  with any client cached against an older API contract. */
  senderName?: string;
  channel?: 'LOBBY' | 'TEAM';
}

export interface ReactionToggleResultData {
  messageId: string;
  emoji: string;
  added: boolean;
  reactions: ReactionTallyData[];
  teamName: string;
  channel: string;
}

export interface LeaderboardEntryData {
  teamId: string;
  teamName: string;
  locationsCompleted: number;
  totalLocations: number;
  totalTimeMs: number;
  // v0.79.39 (2026-05-03) — SIGNED. Positive = net penalty, negative = net
  // pardon. Per-tx clamp at zero was dropped because bonuses past full-pardon
  // silently vanished.
  penaltyMs: number;
  wrongAnswers: number;
  finished: boolean;
}

// --- API Methods ---

export function resolveCode(code: string): Promise<ResolveCodeResponse> {
  return request('GET', `/api/games/resolve?code=${encodeURIComponent(code)}`);
}

export function joinByCode(
  code: string,
  nickname: string,
  isCaptain: boolean,
  /** Session 50 /review merged_bug_005 — optional proof-of-rejoin. When
   *  present and matching an existing Players row, server reuses the row
   *  regardless of nickname case/changes. When absent, server INSERTs a
   *  fresh row and the partial unique index enforces "nickname taken". */
  playerToken?: string
): Promise<JoinGameResponse> {
  const body: Record<string, unknown> = { nickname, isCaptain };
  if (playerToken) body.playerToken = playerToken;
  return request('POST', `/api/games/join?code=${encodeURIComponent(code)}`, body);
}

export function joinGame(code: string, nickname: string, isCaptain: boolean, role: string): Promise<JoinGameResponse> {
  return request('POST', `/api/games/${code}/join`, { nickname, isCaptain, role });
}

export function getGameProgress(code: string, playerId: string, signal?: AbortSignal): Promise<GameProgress> {
  // Session 74 /review: spectator /progress requires X-Player-Token now.
  // Before, the spectator UUID in the query string was sufficient bearer
  // credential — anyone who got hold of the UUID (via logs / screenshots /
  // referer headers) could poll live progress indefinitely. Header auth
  // closes that path.
  return request('GET', `/api/games/${code}/progress?playerId=${encodeURIComponent(playerId)}`, undefined, authHeaders(), signal);
}

export function verifyGeoAnswer(code: string, body: {
  playerId: string;
  locationId: string;
  answerType: string;
  // Preferred: FIAS objectIds (when player picked from autocomplete)
  cityObjectId?: number;
  streetObjectId?: number;
  buildingObjectId?: number;
  // Fallback: raw text (when player typed freely without picking)
  cityText?: string;
  streetText?: string;
  houseText?: string;
  lat?: number;
  lon?: number;
  // Player's actual GPS position at submit time (distinct from answer lat/lon).
  playerLat?: number;
  playerLon?: number;
}): Promise<VerifyGeoResponse> {
  return request('POST', `/api/games/${code}/verify-geo`, body, authHeaders());
}

export function submitAnswer(code: string, body: {
  playerId: string;
  locationId: string;
  answer: string;
  lat?: number;
  lon?: number;
  // Player's actual GPS position at submit time (distinct from answer lat/lon).
  playerLat?: number;
  playerLon?: number;
}): Promise<SubmitAnswerResponse> {
  return request('POST', `/api/games/${code}/answers`, body, authHeaders());
}

/**
 * Advance past the current location without a correct answer. `reason` is:
 * - "TIMEOUT" — always allowed, no penalty (auto-advance on timer expiry).
 * - "SKIP" — requires the game's allowManualSkip flag, adds a penalty equal
 *   to the location's time limit to the player's totalTimeMs.
 */
export function advanceLocation(code: string, body: {
  playerId: string;
  locationId: string;
  reason: 'TIMEOUT' | 'SKIP';
}): Promise<SubmitAnswerResponse> {
  return request('POST', `/api/games/${code}/advance`, body, authHeaders());
}

export function getChatMessages(code: string, playerId: string, signal?: AbortSignal): Promise<ChatMessageData[]> {
  return request('GET', `/api/games/${code}/chat?playerId=${encodeURIComponent(playerId)}`, undefined, authHeaders(), signal);
}

/**
 * /investigate 2026-05-02 — Bug 1 (Edge refresh wiped lobby chat).
 *
 * Player-side lobby chat history. The team-chat helper above filters
 * `channel='TEAM'` server-side, which means lobby messages (player→all,
 * org→all-lobby) had no fetch path on cold mount — `lobby-view` rendered
 * an empty chat after refresh. This pulls the LOBBY-channel rows the
 * player is allowed to see.
 *
 * Server route: `GET /api/games/{code}/chat/lobby` (player-auth via
 * the same token shape `getChatMessages` uses). Returns ASC by timestamp.
 */
export function getLobbyChatHistory(code: string, playerId: string, signal?: AbortSignal): Promise<ChatMessageData[]> {
  return request('GET', `/api/games/${code}/chat/lobby?playerId=${encodeURIComponent(playerId)}`, undefined, authHeaders(), signal);
}

export function sendChatMessage(code: string, playerId: string, message: string, sos = false, mediaUrl?: string): Promise<ChatMessageData> {
  return request('POST', `/api/games/${code}/chat`, { playerId, message, sos, mediaUrl: mediaUrl || null }, authHeaders());
}

/** Session 70 (#13) — toggle an emoji reaction. Request is idempotent in
 *  the «tap twice to undo» sense: first call inserts, second call removes.
 *  Server enforces whitelist of emojis (rejects non-preset with 400). */
export function toggleChatReaction(
  code: string,
  messageId: string,
  playerId: string,
  playerToken: string,
  emoji: string
): Promise<ReactionToggleResultData> {
  return request(
    'POST',
    `/api/games/${code}/chat/${encodeURIComponent(messageId)}/reactions`,
    { playerId, playerToken, emoji },
    authHeaders()
  );
}

/** Session 70 (#13) — server-enforced whitelist mirror. Clients can render
 *  fewer than these, but never more. Keep in sync with
 *  `GameChatOps.REACTION_PRESETS` in backend. */
export const REACTION_PRESETS: readonly string[] = ['👍', '❤️', '😂', '🔥', '👏'] as const;

/** Session 70 /review — mirrors backend `canonicalizeEmoji`. Some IMEs
 *  (Android Samsung Keyboard, iOS voice dictation, terminal paste) strip
 *  the U+FE0F variation selector on «❤️», leaving bare U+2764. Without
 *  canonicalization the WS broadcast's `evt.emoji === '❤'` wouldn't find
 *  the preset pill (`❤️`) and the reaction silently wouldn't render.
 *  NFC by itself doesn't restore a missing VS16 — explicit aliases cover
 *  the known strip-cases. Idempotent: applying twice is a no-op. */
export function canonicalizeEmoji(raw: string): string {
  const nfc = raw.normalize('NFC');
  if (nfc === '\u2764') return '❤️';
  return nfc;
}

export async function uploadChatMedia(file: File): Promise<{ url: string; type: string }> {
  const formData = new FormData();
  formData.append('file', file);
  const headers = authHeaders();
  // Bug 3 — media upload also goes to api.* in prod (gameplay-adjacent).
  const resp = await fetch(`${API_BASE}/api/media/upload`, { method: 'POST', body: formData, headers });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: 'Upload failed' }));
    throw new Error(err.error || 'Upload failed');
  }
  return resp.json();
}

export function getLeaderboard(code: string, playerId: string): Promise<LeaderboardEntryData[]> {
  return request('GET', `/api/games/${code}/leaderboard?playerId=${encodeURIComponent(playerId)}`);
}

// Session 71 — Mission Debrief recap (Ship 2/3). Per-location timings for
// the caller's own team only. Backend auth via playerId + token in the
// query string (matches /player-state pattern) — routes use query-string
// tokens because the endpoint is GET and this keeps the wire shape
// uniform with the other player-scoped reads.
export interface RecapLocationData {
  index: number;
  timeMs: number;
  type: 'correct' | 'timeout' | 'skip' | 'org' | string;
  wrongAnswers: number;
}
export interface PlayerRecapData {
  locations: RecapLocationData[];
  teamStartedAt: number | null;
  teamFinishedAt: number | null;
  /** /investigate 2026-04-27 prod 125908 — team-scoped wrong total for the
   *  results-screen summary card. Pre-fix the summary used the player's
   *  local `state.wrongAnswers`, which only counted wrongs THIS player
   *  fired. Two captains saw different numbers (1 vs 0) for the same team. */
  teamWrongAnswers?: number;
}
export function getPlayerRecap(code: string, playerId: string, playerToken: string): Promise<PlayerRecapData> {
  // Session 74 /review: token moved from `?token=` query-string to
  // X-Player-Token header so it doesn't leak into browser history,
  // proxy URL logs, or referer headers. Backend accepts either for one
  // release window; later versions drop the query-string fallback.
  const qs = `playerId=${encodeURIComponent(playerId)}`;
  return request(
    'GET',
    `/api/games/${code}/recap?${qs}`,
    undefined,
    { 'X-Player-Token': playerToken }
  );
}

export function hintNavigate(code: string, playerId: string, locationId: string): Promise<{ lat: number; lon: number; city?: string; street?: string; house?: string }> {
  return request('POST', `/api/games/${code}/hint-navigate`, { playerId, locationId }, authHeaders());
}

export function leaveGame(code: string, playerId: string): Promise<void> {
  return request('DELETE', `/api/games/${code}/players/${playerId}`);
}

// WebSocket URL builder. Appends the player token as a query parameter so
// the backend can verify the caller on handshake — browsers can't set custom
// headers on WebSocket upgrades. Session 32 cso finding #7.
// Callers pass a path like "/ws/games/{code}/chat?playerId=..." and this
// function adds the token parameter.
export function wsUrl(path: string): string {
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  const token = getState().playerToken;
  if (!token) {
    return `${proto}//${location.host}${path}`;
  }
  const sep = path.includes('?') ? '&' : '?';
  return `${proto}//${location.host}${path}${sep}token=${encodeURIComponent(token)}`;
}

/* ── Session 50 §5e.bis — offline sync reconciliation ──────────────── */

export interface SyncLocationEntry {
  locationId: string;
  submittedAnswer: string;
  wasSkip?: boolean;
  completedAt?: number;
}

export interface SyncRequestBody {
  playerId: string;
  locationsCompleted: SyncLocationEntry[];
}

export interface SyncResponse {
  authoritativeIndex: number;
  caughtUp: boolean;
  accepted: number;
  rejected: number;
  penaltyMs: number;
  finishedAt?: number | null;
  nextRiddleKey?: string | null;
  nextEncryptedRiddle?: string | null;
  nextEncryptedHint?: string | null;
  nextEncryptedHints?: EncryptedHintData[];
}

/**
 * POST /api/games/{code}/sync — flush the offline queue on reconnect.
 * Server re-verifies every answer and returns authoritative team state,
 * including `nextRiddleKey` for the landing location (session-44 class).
 * Rate-limited on the server to 1 call per 5s per playerToken; a 429 here
 * means "try again in a moment" and the caller should NOT clear the queue.
 */
export function syncOfflineProgress(
  code: string,
  body: SyncRequestBody
): Promise<SyncResponse> {
  return request<SyncResponse>('POST', `/api/games/${code}/sync`, body, authHeaders());
}

/* ── Organizer auth (embedded in PWA join-view) ────────────────────── */

export interface AuthCapabilities {
  emailConfigured: boolean;
  aiConfigured: boolean;
  hasYandexOAuth: boolean;
  hasGoogleOAuth: boolean;
}

export async function getCapabilities(): Promise<AuthCapabilities> {
  // Bug 3 (v0.79.34) — /api/auth/* INTENTIONALLY stays on parent origin
  // (relative path, no API_BASE prefix). Reason: org_session cookie is
  // SameSite=Lax + host-bound to autokvest.ru — moving to api.* would
  // need allowCredentials + cookie Domain=.autokvest.ru rework. F2 scope
  // covers gameplay only. Don't "fix" this without re-doing cookie work.
  const res = await fetch('/api/auth/capabilities');
  return res.json();
}

/** Returns null on success (cookie is set by the server). Throws on error. */
export async function orgLogin(email: string, password: string): Promise<void> {
  // Bug 3 (v0.79.34) — see comment in getCapabilities above. /api/auth/*
  // stays on parent origin to preserve cookie semantics.
  const res = await fetch('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
    throw new Error(body.error);
  }
}
